import React, { MouseEvent, ReactNode, useId } from "react"
import {
  Box, Flex, HStack, Radio, Text, useRadioGroupContext,
} from "@chakra-ui/react"
import Icon from "components/elements/Icon"

type Props = {
  value: string
  title: string
  description?: ReactNode
  icon?: string
  badge?: ReactNode
  children?: ReactNode
}

const RadioCard = ({
  value, title, description, icon, badge, children,
}: Props) => {
  const group = useRadioGroupContext()
  const selected = group?.value === value
  const titleId = useId()
  const descriptionId = useId()

  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    if (!(event.target as HTMLElement).closest("a, button, input, select, textarea, label")) {
      group?.onChange(value)
    }
  }

  return (
    <Box
      onClick={handleClick}
      p={4}
      borderWidth={1}
      borderRadius="md"
      borderColor={selected ? "blue.400" : "gray.300"}
      bg={selected ? "blue.50" : "white"}
      cursor="pointer"
      position="relative"
      zIndex={selected ? 1 : undefined}
      transition="border-color 0.15s, background 0.15s"
      _hover={selected ? undefined : { borderColor: "gray.400" }}
    >
      <Flex gap={3} align="flex-start">
        <Radio value={value} mt={icon ? 1.5 : 0.5} inputProps={{ "aria-labelledby": titleId, "aria-describedby": description ? descriptionId : undefined }} />
        {icon && (
          <Flex
            boxSize={8}
            flexShrink={0}
            align="center"
            justify="center"
            bg="white"
            borderWidth={1}
            borderColor="gray.200"
            borderRadius="md"
            color={selected ? "blue.500" : "gray.500"}
          >
            <Icon icon={icon} size={4} />
          </Flex>
        )}
        <Box flex={1} minW={0} pt={icon ? 1 : 0}>
          <HStack spacing={2}>
            <Text id={titleId} fontWeight="semibold" lineHeight="short">{title}</Text>
            {badge}
          </HStack>
          {description && <Text id={descriptionId} mt={1}>{description}</Text>}
          {children}
        </Box>
      </Flex>
    </Box>
  )
}

export default RadioCard
